import { Fragment, useRef, useState } from "react";
import moment from "moment/moment";
import functions from "../../functions/functions";
import Origins from "../icons/Origins";
import PrinterIcon from "../../icons/PrinterIcon";
import InvoiceIcon from "../../icons/InvoiceIcon";
import QrIcon from "../../icons/QrIcon";
import MailIcon from "../../icons/MailIcon";
import Takeaway from "../../icons/Takeaway";
import Delivery from "../../icons/Delivery";
import BarIcon from "../../icons/BarIcon";
import TableIcon from "../../icons/Table";
import { billErrorText, paymentErrorSummary } from "../../functions/paymentErrors";

function getThermalPrintStatusClass(success) {
	if (Number(success) === 1) return "success";
	if (Number(success) === 0) return "error";
	return "warning";
}

function getThermalPrintStatusLabel(result) {
	if (Number(result?.success) === 1) return "OK";
	const payloadStatus = String(result?.payload_json?.status || "").trim();
	if (payloadStatus === "no_products") return "Nessun prodotto";
	if (payloadStatus === "failed") return "Fallita";
	const errorCode = String(result?.error_code || "").trim();
	if (errorCode === "printer_not_connected") return "Stampante offline";
	if (errorCode === "print_failed") return "Errore stampa";
	if (errorCode === "no_products") return "Nessun prodotto";
	return "Errore";
}

function getThermalPrintSummaries(order) {
	const results = Array.isArray(order?.thermal_print_results) ? order.thermal_print_results : [];
	if (results.length === 0) return [];

	const grouped = new Map();
	for (const result of results) {
		const printerId = result?.thermal_printer_id ?? result?.printer_name ?? result?.id;
		if (!grouped.has(printerId)) grouped.set(printerId, []);
		grouped.get(printerId).push(result);
	}

	return Array.from(grouped.values()).map((group) => {
		const sortedGroup = [...group].sort((a, b) => {
			const attemptA = Number(a?.attempt || 0);
			const attemptB = Number(b?.attempt || 0);
			if (attemptA !== attemptB) return attemptB - attemptA;
			return String(b?.updated_at || "").localeCompare(String(a?.updated_at || ""));
		});
		const latest = sortedGroup[0] || {};
		const printerName = String(latest?.printer_name || latest?.printer_ip || "Stampante").trim();
		const statusLabel = getThermalPrintStatusLabel(latest);
		const attempt = Number(latest?.attempt || 0);
		const errorMessage = String(latest?.error_message || "").trim();
		const payloadStatus = String(latest?.payload_json?.status || "").trim();
		const productsCount = Number(latest?.payload_json?.printed_products_count || 0);
		const flowsCount = Number(latest?.payload_json?.flows_count || 0);
		const details = [];

		if (attempt > 0) details.push("Tentativi: " + attempt);
		if (payloadStatus === "printed" && productsCount > 0) details.push(productsCount + " prodotti");
		if (payloadStatus === "printed" && flowsCount > 1) details.push(flowsCount + " flussi");
		if (errorMessage) details.push(errorMessage);

		return {
			key: latest?.id ?? printerId,
			printerName,
			statusLabel,
			statusClass: getThermalPrintStatusClass(latest?.success),
			details,
		};
	});
}

function Table({
	header = false,
	rows = [],
	cols = [],
	showProducts = false,
	repeatHeader = false,
	smallHeader = false,
	type,
	draggableRows = false,
	onRowReorder = null,
	rowKey = "id",
	highlightedRowId = null,
}) {
	const dragRowRef = useRef(null);
	const [draggingRowIdentity, setDraggingRowIdentity] = useState(null);
	const [dragOverRowIdentity, setDragOverRowIdentity] = useState(null);
	const [expandedPrintRows, setExpandedPrintRows] = useState(new Set());

	const getGridTemplateColumns = (cols) => {
		const visibleCols = cols.filter((col) => checkVisibility(col.visible));
		const template = visibleCols.map((col) => {
			if (col.width !== undefined && col.width !== null) {
				if (typeof col.width === "number") return `${col.width}fr`;
				return col.width;
			}
			return "minmax(0, 1fr)";
		});
		return template.join(" ");
	};

	const checkVisibility = (visible, row = null) => {
		if (visible == undefined) return true;
		if (visible === true) return true;
		if (typeof visible === "function") return row === null ? true : visible(row);
	};

	const checkChildVisibility = (child) => {
		if (child.status == "archived") return false;
		if (type == "sells" && [1, 2, 3, 4].find((el) => el == child.bill)) return true;
		if (type == "orders" && (child.bill == 0 || child.bill == null)) return true;
		return false;
	};

	const canDragRows = draggableRows && typeof onRowReorder === "function";
	const getRowIdentity = (row) => {
		if (rowKey && row?.[rowKey] !== undefined && row?.[rowKey] !== null) return row[rowKey];
		return row?.id;
	};
	const onDragStartRow = (row) => (event) => {
		if (!canDragRows) return;
		dragRowRef.current = row;
		setDraggingRowIdentity(getRowIdentity(row));
		setDragOverRowIdentity(null);
		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData("text/plain", String(getRowIdentity(row)));
	};
	const onDragOverRow = (targetRow) => (event) => {
		if (!canDragRows) return;
		event.preventDefault();
		event.dataTransfer.dropEffect = "move";
		const targetIdentity = getRowIdentity(targetRow);
		if (
			draggingRowIdentity !== null &&
			String(targetIdentity) !== String(draggingRowIdentity) &&
			String(targetIdentity) !== String(dragOverRowIdentity)
		)
			setDragOverRowIdentity(targetIdentity);
	};
	const onDragEnterRow = (targetRow) => () => {
		if (!canDragRows) return;
		const targetIdentity = getRowIdentity(targetRow);
		if (draggingRowIdentity === null) return;
		if (String(targetIdentity) === String(draggingRowIdentity)) return;
		if (String(targetIdentity) !== String(dragOverRowIdentity)) setDragOverRowIdentity(targetIdentity);
	};
	const onDropRow = (targetRow) => (event) => {
		if (!canDragRows) return;
		event.preventDefault();
		const draggedRow = dragRowRef.current;
		dragRowRef.current = null;
		setDragOverRowIdentity(null);
		setDraggingRowIdentity(null);
		if (!draggedRow) return;
		if (String(getRowIdentity(draggedRow)) === String(getRowIdentity(targetRow))) return;
		onRowReorder(draggedRow, targetRow);
	};
	const onDragEndRow = () => {
		dragRowRef.current = null;
		setDragOverRowIdentity(null);
		setDraggingRowIdentity(null);
	};

	const togglePrintRow = (row) => {
		const key = getRowIdentity(row);
		setExpandedPrintRows((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	return (
		<div
			className={"grid-table" + (type ? " grid-table-" + type : "")}
			style={{
				gridTemplateColumns: getGridTemplateColumns(cols),
			}}
		>
			{header && !repeatHeader && <Header cols={cols} smallHeader={smallHeader} />}
			{rows.map((row, i) => (
				<Fragment key={i}>
					<Row
						header={header}
						repeatHeader={repeatHeader}
						cols={cols}
						row={row}
						tableType={type}
						showProducts={showProducts}
						smallHeader={smallHeader}
						highlightedRowId={highlightedRowId}
						draggableRows={canDragRows}
						isDragSource={
							canDragRows &&
							draggingRowIdentity !== null &&
							String(draggingRowIdentity) === String(getRowIdentity(row))
						}
						isDropTarget={
							canDragRows &&
							dragOverRowIdentity !== null &&
							String(dragOverRowIdentity) === String(getRowIdentity(row)) &&
							String(draggingRowIdentity) !== String(getRowIdentity(row))
						}
						onRowDragStart={onDragStartRow(row)}
						onRowDragOver={onDragOverRow(row)}
						onRowDragEnter={onDragEnterRow(row)}
						onRowDrop={onDropRow(row)}
						onRowDragEnd={onDragEndRow}
						expandedPrintRows={expandedPrintRows}
						onTogglePrintRow={togglePrintRow}
						getRowIdentity={getRowIdentity}
					/>
					{row.childs &&
						row.childs.map(
							(child, n) =>
								checkChildVisibility(child) && (
									<Fragment key={n}>
										<Row
											header={header}
											repeatHeader={repeatHeader}
											cols={cols}
											row={child}
											tableType={type}
											showProducts={showProducts}
											smallHeader={smallHeader}
											customClass="child"
										/>
									</Fragment>
								)
						)}
					{row.merge_childs &&
						row.merge_childs.map((child, n) => (
							<Fragment key={`merge-${n}`}>
								<Row
									header={header}
									repeatHeader={repeatHeader}
									cols={cols}
									row={child}
									tableType={type}
									showProducts={showProducts}
									smallHeader={smallHeader}
									customClass="child"
								/>
							</Fragment>
						))}
				</Fragment>
			))}
		</div>
	);
}

function Header({ cols, smallHeader }) {
	const checkVisibility = (visible) => {
		if (visible == undefined) return true;
		if (visible === true) return true;
		if (typeof visible === "function") return true;
	};
	return (
		<>
			{cols.map(
				(col, i) =>
					checkVisibility(col.visible) && (
						<div
							key={i}
							className={
								"grid-table-header" +
								(smallHeader ? " small" : "") +
								(col.align ? " " + col.align : "")
							}
						>
							{col.label}
						</div>
					)
			)}
		</>
	);
}

function Row({
	header,
	repeatHeader,
	cols,
	row,
	tableType,
	showProducts,
	smallHeader,
	customClass = "",
	highlightedRowId = null,
	draggableRows = false,
	isDragSource = false,
	isDropTarget = false,
	onRowDragStart,
	onRowDragOver,
	onRowDragEnter,
	onRowDrop,
	onRowDragEnd,
	expandedPrintRows = new Set(),
	onTogglePrintRow,
	getRowIdentity = (row) => row?.id,
}) {
	const checkVisibility = (visible, row = null) => {
		if (visible == undefined) return true;
		if (visible === true) return true;
		if (typeof visible === "function") return visible(row);
	};

	function getBillCell(order) {
		const statusClass = getBillClass(order);
		const icon = order.bill_url ? (
			<a href={order.bill_url} target="_blank" className={statusClass}>
				{getBillIcon(order)}
			</a>
		) : (
			<div className={statusClass}>{getBillIcon(order)}</div>
		);

		return (
			<div className="bill-cell-status">
				{icon}
				<div className="bill-result-details">
					{getBillDetails(order).map((detail, index) => (
						<div
							key={index}
							className={"bill-result-line" + (statusClass === "error" ? " error" : "")}
						>
							{detail}
						</div>
					))}
				</div>
			</div>
		);
	}

	function getInvoiceResultStatus(order) {
		const result = order?.invoice_result;
		if (!result) return null;
		const serviceOk = result?.service_response?.errorCode === "0000";
		const arubaOk = result?.aruba_response?.status === "inviata";
		return { isOk: serviceOk && arubaOk, serviceOk, arubaOk, result };
	}

	function getBillClass(order) {
		if (order.invoice == 1) {
			const inv = getInvoiceResultStatus(order);
			if (!inv) return "info";
			return inv.isOk ? "success" : "error";
		}
		if (order.bill == 3) return "success";
		if (order.bill == 1) return "warning";
		if (order.bill == 2) return "error";
		if (order.bill == 4) return "info";
	}

	function getBillIcon(order) {
		if (order.invoice == 1) return <InvoiceIcon />;
		if (order.erecipt == 1) return <MailIcon />;
		if (order.erecipt == 2) return <QrIcon />;
		else return <PrinterIcon />;
	}

	function getBillLabel(order) {
		if (order.bill == 3) return "OK";
		if (order.bill == 2) return "Errore";
		if (order.bill == 1) return "In corso";
		if (order.bill == 4) return "Fattura";
		return "";
	}

	function getBillDetails(order) {
		if (order.invoice == 1) {
			const inv = getInvoiceResultStatus(order);
			if (!inv) return ["Fattura"];
			if (inv.isOk) return ["OK"];
			const details = [];
			if (!inv.serviceOk && inv.result?.service_response?.errorDescription) {
				details.push(inv.result.service_response.errorDescription);
			}
			if (!inv.arubaOk && inv.result?.aruba_response?.status) {
				details.push("Aruba: " + inv.result.aruba_response.status);
			}
			return details.length > 0 ? details : ["Errore fattura"];
		}
		// con bill == 2 bill_number e' il solo codice di errore scritto dall'agente: lo si traduce
		const billNumber = order?.bill == 2 ? billErrorText(order?.bill_number) : order?.bill_number;
		const details = [billNumber, order?.fiskaly_id]
			.filter((value) => String(value || "").trim())
			.map((value) => String(value).trim());

		if (details.length > 0) return details;
		const fallbackLabel = getBillLabel(order);
		return fallbackLabel ? [fallbackLabel] : [];
	}

	function getThermalPrintCell(order, expanded, onToggle) {
		const summaries = getThermalPrintSummaries(order);
		if (summaries.length === 0) {
			return <div className="thermal-print-empty">nessun esito stampa disponibile</div>;
		}

		if (!expanded) {
			return (
				<button
					type="button"
					className="thermal-print-collapsed"
					onClick={onToggle}
					aria-expanded={false}
				>
					<div className="thermal-print-badge-row">
						{summaries.map((summary) => (
							<span key={summary.key} className={"thermal-print-badge " + summary.statusClass}>
								{summary.statusLabel}
							</span>
						))}
					</div>
					<span className="thermal-print-arrow">▾</span>
				</button>
			);
		}

		return (
			<div className="thermal-print-expanded">
				<button
					type="button"
					className="thermal-print-collapsed"
					onClick={onToggle}
					aria-expanded={true}
				>
					<div className="thermal-print-badge-row">
						{summaries.map((summary) => (
							<span key={summary.key} className={"thermal-print-badge " + summary.statusClass}>
								{summary.statusLabel}
							</span>
						))}
					</div>
					<span className="thermal-print-arrow">▴</span>
				</button>
				<div className="thermal-print-results">
					{summaries.map((summary) => (
						<div key={summary.key} className="thermal-print-result">
							<div className="thermal-print-head">
								<span className="thermal-print-printer">{summary.printerName}</span>
								<span className={"thermal-print-badge " + summary.statusClass}>
									{summary.statusLabel}
								</span>
							</div>
							{summary.details.map((detail, index) => (
								<div key={index} className={"thermal-print-detail " + summary.statusClass}>
									{detail}
								</div>
							))}
						</div>
					))}
				</div>
			</div>
		);
	}

	function getPayedLabel(order) {
		if (order.status == "payment_error") {
			// causa scritta dall'agente di stampa (payment_error_message / payment_error_code), se disponibile
			const cause = paymentErrorSummary(order);
			return (
				<div className="error" title={cause || undefined}>
					errore
					{cause && (
						<small style={{ display: "block", fontSize: "10px", fontWeight: 400, lineHeight: 1.2, color: "#b3261e" }}>
							{cause}
						</small>
					)}
				</div>
			);
		}
		if (order.status == "awaiting_payment")
			return <div className="error">{getStatusLabel(order.status)}</div>;
		else if (order.payment_method?.prepaid == 1) return <div className="success">SI</div>;
		else return <div className="error">NO</div>;
	}

	function getStatusLabel(status) {
		if (status == "confirmed") return "Confermato";
		else if (status == "payment_error") return "Errore pagamento";
		else if (status == "awaiting_payment") return "In attesa di pagamento";
		else if (status == "archived") return "Annullato";
		else if (status == "preparation") return "In preparazione";
		else if (status == "filled") return "Evaso";
		else if (status == "ready") return "Pronto";
		else return status;
	}

	function getInventoryStatus(code) {
		if (code === 0) return "EMPTY";
		if (code === 1) return "NEAR_EMPTY";
		if (code === 2) return "EXIST";
		if (code === 3) return "NEAR_FULL";
		if (code === 4) return "FULL";
		if (code === 21) return "Missing";
		if (code === 22) return "NA";
		return "Unknown (" + code + ")";
	}

	function getStatusClass(status) {
		return "order-status " + status;
	}

	function getDeliveryType(order) {
		if (order.delivery_type == "delivery") return <Delivery />;
		if (order.delivery_type == "takeaway") return <Takeaway />;
		if (order.delivery_type == "shop" && order.table_id) return <TableIcon />;
		if (order.delivery_type == "shop" && !order.table_id) return <BarIcon />;
	}

	function getInventoryStatusLabel(code) {
		if (code == 0) return <div className="error">{getInventoryStatus(code)}</div>;
		else if (code == 1) return <div className="warning">{getInventoryStatus(code)}</div>;
		else return <div className="success">{getInventoryStatus(code)}</div>;
	}

	function getBorderColor(h) {
		if (draggableRows) return {};
		if (h != 0) return {};
		const color = functions.pastelColorFromInt(row.parent_id ? row.parent_id : row.id);
		return {
			borderLeft: "5px solid " + color,
		};
	}

	function getCellClassName(col, h) {
		const classes = [];
		classes.push("table-cell");
		if (col.type) classes.push("table-cell-" + col.type);
		if (tableType) classes.push("table-cell-" + tableType);
		if (col.align) classes.push(col.align);
		if (customClass) classes.push(customClass);
		if (draggableRows && h === 0) classes.push("row-drag-handle");
		if (draggableRows) classes.push("row-reorderable");
		if (isDragSource) classes.push("row-dragging");
		if (isDropTarget) classes.push("row-drag-over");
		if (
			highlightedRowId !== null &&
			highlightedRowId !== undefined &&
			String(row?.id) === String(highlightedRowId)
		)
			classes.push("row-highlighted");
		return classes.join(" ");
	}

	return (
		<>
			{header && repeatHeader && <Header cols={cols} smallHeader={smallHeader} />}
			{cols.map(
				(col, h) =>
					checkVisibility(col.visible, row) && (
						<div
							key={h}
							className={getCellClassName(col, h)}
							style={getBorderColor(h)}
							data-row-id={row?.id}
							draggable={draggableRows && h === 0}
							onDragStart={draggableRows && h === 0 ? onRowDragStart : undefined}
							onDragOver={draggableRows ? onRowDragOver : undefined}
							onDragEnter={draggableRows ? onRowDragEnter : undefined}
							onDrop={draggableRows ? onRowDrop : undefined}
							onDragEnd={draggableRows && h === 0 ? onRowDragEnd : undefined}
							title={draggableRows && h === 0 ? "Trascina per riordinare" : undefined}
						>
							{draggableRows && h === 0 && (
								<span className="row-drag-grip" aria-hidden="true">
									<span className="row-drag-grip-dots"></span>
								</span>
							)}
							<div className={draggableRows && h === 0 ? "row-drag-content" : undefined}>
								{col.type == "text" && <>{row[col.id]}</>}
								{col.type == "id" && (
									<>
										{tableType === "orders" || tableType === "sells" ? (
											<>
												<div>
													<b>{"ID: " + row.id}</b>
												</div>
												{row.number && (
													<div className="row-id-number">{"N. " + row.number}</div>
												)}
											</>
										) : (
											<>
												{row.number && <div>{"N." + row.number}</div>}
												<div>{"ID:" + row.id}</div>
											</>
										)}
									</>
								)}
								{col.type == "delivery_type" && <>{getDeliveryType(row)}</>}
								{col.type == "delivery_date" && (
									<>
										<div>
											{row.delivery_date &&
												moment(row.delivery_date, "YYYY-MM-DD HH:mm:ss").format(
													"HH:mm"
												)}
										</div>
									</>
								)}
								{col.type == "origin" && (
									<Origins origin={row.origin} color="#000000" />
								)}
								{col.type == "customer" && (
									<>
										{tableType === "orders" || tableType === "sells" ? (
											<div className="small">
												{(() => {
													const name = String(row?.name || "").trim();
													const surname = String(row?.surname || "").trim();
													return [name, surname].filter(Boolean).join(" ");
												})()}
											</div>
										) : (
											<>
												<div className="small">
													{row.name && row.name} {row.surname && row.surname}
												</div>
												{row.mobile && (
													<div className="small">{"Tel: " + row.mobile}</div>
												)}
											</>
										)}
									</>
								)}
								{col.type == "user" && (
									<>
										{[row?.user?.name, row?.user?.surname]
											.filter((value) => String(value || "").trim())
											.join(" ")}
									</>
								)}
								{col.type == "info" && (
									<>
										<div className="small">
											<span className={getStatusClass(row.status)}>
												{getStatusLabel(row.status)}
											</span>
										</div>
										{row.table_id && (
											<>
												<div className="small">Sala: {row.room?.name}</div>
												<div className="small">Tavolo: {row.table.name}</div>
											</>
										)}
										<div className="small">
											Listino: {row.list_id ? row.list.name : "base"}
										</div>
										{row.romana_counter && (
											<div className="small">
												Pagamento romana: {row.romana_counter}
											</div>
										)}
										{row.merge_id && (
											<div className="small">
												Unito con ordine: #{row.merge_id}
											</div>
										)}
										{row.delivery_type == "delivery" && row.delivery_address && (
											<>
												<div className="small">
													{row.delivery_address.address +
														" " +
														row.delivery_address.number}
												</div>
												<div className="small">
													{row.delivery_address.zipcode +
														" " +
														row.delivery_address.city}
												</div>
											</>
										)}
									</>
								)}
								{col.type == "eur" && (
									<div>
										{functions.formatter.format(
											col.multiply ? row[col.id] * col.multiply : row[col.id]
										)}
									</div>
								)}
								{col.type == "bool" && (
									<>
										{row[col.id] == 1 ? (
											<div className="success">SI</div>
										) : (
											<div className="error">NO</div>
										)}
									</>
								)}
								{col.type == "toggle" && (
									<button
										type="button"
										className={
											"table-switch" + (Number(row[col.id]) === 1 ? " is-yes" : " is-no")
										}
										onClick={() =>
											typeof col.onChange === "function" &&
											col.onChange(row, Number(row[col.id]) === 1 ? 0 : 1)
										}
										aria-pressed={Number(row[col.id]) === 1}
									>
										<div className="table-switch-track">
											<span className="table-switch-label yes">SI</span>
											<span className="table-switch-label no">NO</span>
											<span className="table-switch-knob"></span>
										</div>
									</button>
								)}
								{col.type == "checkbox" && !(typeof col.isHidden === "function" && col.isHidden(row)) && (
									<input
										type="checkbox"
										checked={typeof col.isChecked === "function" ? col.isChecked(row) : false}
										disabled={typeof col.isDisabled === "function" ? col.isDisabled(row) : false}
										onChange={() => typeof col.onChange === "function" && col.onChange(row)}
										onClick={(e) => e.stopPropagation()}
										title={typeof col.getTitle === "function" ? col.getTitle(row) : undefined}
									/>
								)}
								{col.type == "prepaid" && <>{getPayedLabel(row)}</>}
								{col.type == "inventory_status" && (
									<>{getInventoryStatusLabel(row[col.id])}</>
								)}
								{col.type == "bill" && (
									<>
										{getBillCell(row)}
									</>
								)}
								{col.type == "thermal_print_results" && (
									<>
										{getThermalPrintCell(
											row,
											expandedPrintRows.has(getRowIdentity(row)),
											() => onTogglePrintRow && onTogglePrintRow(row)
										)}
									</>
								)}
								{col.type == "buttons" && !row.merge_id && (
									<div
										style={{
											display: "flex",
											gap: "5px",
											width: col.align === "right" ? "100%" : "auto",
											justifyContent:
												col.align === "right" ? "flex-end" : "flex-start",
										}}
									>
										{col.buttons.map(
											(button, n) =>
												checkVisibility(button.visible, row) && (
													<button
														key={n}
														onClick={() => button.onClick(row)}
														className={
															"button " +
															(button.className ? button.className : "button-1") +
															(button.icon ? " square icon" : "")
														}
													>
														{button.label && button.label}
														{button.icon && button.icon}
													</button>
												)
										)}
									</div>
								)}
							</div>
						</div>
					)
			)}
			{showProducts &&
				row.products &&
				row.products.map((product, n) => (
					<div key={n} style={{ gridColumn: "span " + cols.length }}>
						<div className="horizzontal-group large">
							{product.qty}x {product.name}
							<div className="horizzontal-group">
								<div>{functions.formatter.format(product.price)}</div>
								<div>{functions.formatProductStatus(product.status)}</div>
							</div>
						</div>
					</div>
				))}
		</>
	);
}

export default Table;
